import logger from 'ws-scripts/modules/logger';
import {
	addNewRelicPageAction,
	setNewRelicCustomAttribute
} from '@ddc/new-relic';
import { hysteriFetch, wrappers } from 'hysterics';
import { isBrowser } from 'ws-scripts/modules/environment';
import { applyDefaultSortBy } from 'wsm-wis-client';
import { publishEvent } from './pubsub';
import { emptyFacetObj, emptyInventoryObj } from '../global-constants';
import { updateDdcDataLayerFromWIS, setDataLayerTracking } from './tracking';
import { processWISResponse } from './processWISResponse';

const requestInventoryAndFacetsWrapper = hysteriFetch(
	'ws-inv-data-request-get-inventory-and-facets'
);

export const trackFetchDuration = (fetchType, endpoint) => {
	const matchingEntries = performance
		.getEntriesByType('resource')
		.filter((resource) => resource.name.includes(fetchType));
	const numRequests = matchingEntries.length;
	const timing = matchingEntries[numRequests - 1];

	const message = `${fetchType} | ${endpoint} | duration: ${timing?.duration}, request: ${numRequests}`;
	logger.info(message);

	addNewRelicPageAction('WS INV DATA FETCH DURATION', {
		srpFetchType: fetchType,
		srpFetchEndpoint: endpoint,
		srpFetchCount: numRequests,
		srpFetchDuration: timing?.duration
	});
};

/**
 * Unified request for both inventory and facets data in a single API call
 * Reduces network requests and improves page load performance by fetching
 * both datasets simultaneously while preserving existing event publishing patterns
 *
 * @param {Object} invParams - Inventory parameters from URL/filters
 * @param {Object} requestBody - WIS request body object (from wsm-wis-client
 *   buildWisRequestBody)
 * @param {string} eventType - Type of event triggering this request (search, facet, etc.)
 * @param {Object} eventData - Additional event data for tracking
 * @param {Object} [flags] - Feature flags; read client-side only (e.g. the
 *   `srp-track-fetch-resource-timing` diagnostic). Not sent in the WIS body —
 *   the proxy strips flags, so they never need to travel through it.
 */
export const requestInventoryAndFacets = (
	invParams,
	requestBody,
	eventType,
	eventData,
	flags = {}
) => {
	(async () => {
		let facetResponse;
		let inventoryResponse;
		let unifiedResponse;
		let errorMessage;
		let errorStack;
		// DE748298: marks the synchronous stage in progress so a thrown error
		// (e.g. "Maximum call stack size exceeded") can be localized in NR.
		let failedStage = 'request';
		// Apply the default sort ONCE: applyDefaultSortBy returns a NEW object, so
		// the post-default sortBy must be read from here for both the fetch body
		// and the dataLayer tracking call below. Reading sortBy off the original
		// `invParams` instead would report `undefined` for an explicit user sort
		// (the cloned request body no longer mutates invParams) and for the
		// implicit geodist default — analytics-only, but a silent miss either way.
		const requestWithDefaults = applyDefaultSortBy(requestBody);

		try {
			publishEvent('ws-inv-data/inventory-request');

			// Make unified request to new endpoint
			failedStage = 'fetch';
			unifiedResponse = await requestInventoryAndFacetsWrapper(
				'/api/widget/ws-inv-data/getInventoryAndFacets',
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify(requestWithDefaults)
				},
				{
					// Hardcoded browser->proxy timeout. Must stay ABOVE the server's
					// proxy->WIS timeout (3000ms in wisFetcher.js) so the inner fetch
					// resolves first and the proxy surfaces a real 504/502, rather than
					// the browser aborting a still-in-flight request. Was the
					// `ws-inv-data-wis-fetch-timeout` flag (default 5000); hardcoded
					// with the profiles (TA4082507) so the invariant can't be broken by
					// a flag edit and an unset flag can't pass `undefined`.
					[wrappers.timeout.contextKey]: {
						timeout: 5000
					}
				}
			);

			if (
				unifiedResponse &&
				typeof unifiedResponse === 'object' &&
				(Array.isArray(unifiedResponse.facets) ||
					Array.isArray(unifiedResponse.inventory))
			) {
				failedStage = 'process-response';
				// Shape facets + inventory via the shared processWISResponse — the
				// same normalizer the SSR-hydration path uses, so the two can't drift
				// (issue #341). The geo-facet gate lives in the helper: geoData is
				// defined iff geoLocationEnabled, and it prepends the location facet
				// so SSR and client-fallback render the same facet UI on a no-geo page
				// rather than diverging on geoRadius presence. Absent facets/inventory
				// stay undefined so the "missing data" branches below still fire.
				({ facetResponse, inventoryResponse } = processWISResponse(
					unifiedResponse,
					{ geoEnabled: isBrowser && !!window.DDC?.InvData?.geoData }
				));

				// ===================================================
				// SECTION 1: Publish Facets Response
				// ===================================================
				if (facetResponse.facets) {
					if (isBrowser && window.DDC.InvData) {
						window.DDC.InvData.facets = facetResponse;
						window.DDC.InvData.appliedFilters =
							facetResponse?.filters || [];
						failedStage = 'publish-facets';
						publishEvent('ws-inv-data/facets');
					}
				} else {
					errorMessage = 'missing facet data';
					if (isBrowser && window.DDC.InvData) {
						window.DDC.InvData.facets = emptyFacetObj;
					}
				}

				// ===================================================
				// SECTION 2: Publish Inventory Response
				// ===================================================
				if (inventoryResponse.inventory) {
					if (isBrowser && window.DDC.InvData) {
						window.DDC.InvData.inventory = inventoryResponse;
						failedStage = 'publish-inventory';
						publishEvent('ws-inv-data/inventory');
					}
				} else {
					errorMessage = 'missing inventory data';
					if (isBrowser && window.DDC.InvData) {
						window.DDC.InvData.inventory = emptyInventoryObj;
					}
				}

				// ===================================================
				// SECTION 3: Fire Tracking Events
				// ===================================================
				// Only fire tracking if we have both facets and inventory data
				if (
					isBrowser &&
					window.DDC.InvData &&
					facetResponse.facets &&
					inventoryResponse.inventory
				) {
					const {
						pageInfo: { trackingData, ...pageInfo }
					} = inventoryResponse;

					failedStage = 'tracking';
					setDataLayerTracking(
						window.DDC.InvData.appliedFilters,
						window.DDC.InvData.sortBy,
						{
							pageInfo,
							trackingData
						},
						eventType,
						eventData
					);

					updateDdcDataLayerFromWIS(
						inventoryResponse,
						requestWithDefaults.inventoryParameters?.sortBy
					);
				}
			} else {
				// Handle null/undefined unified response or completely empty response
				errorMessage = 'missing inventory and facet data';
				if (isBrowser && window.DDC.InvData) {
					window.DDC.InvData.facets = emptyFacetObj;
					window.DDC.InvData.inventory = emptyInventoryObj;
				}
			}
		} catch (error) {
			// Log unified request failure
			errorMessage = error.message || error;
			errorStack = error.stack;
		} finally {
			// Track fetch duration if enabled
			if (flags['srp-track-fetch-resource-timing']) {
				trackFetchDuration('getInventoryAndFacets', 'WIS');
			}

			// Publish failure event if we don't have inventory
			if (!inventoryResponse?.inventory) {
				publishEvent('ws-inv-data/request-failed');
			}

			// Log error to New Relic if one occurred
			if (errorMessage) {
				setNewRelicCustomAttribute(
					'SRP ERROR',
					`ws-inv-data unified request failed: ${errorMessage}`
				);
				addNewRelicPageAction('WS INV DATA UNIFIED REQUEST FAILED', {
					errorMsg: errorMessage,
					errorStack,
					failedStage,
					hasFacets: !!facetResponse?.facets,
					hasInventory: !!inventoryResponse?.inventory
				});
			}
		}
	})();
};
